// Client API — fetch wrapper com credentials e error handling padrão.
import type { FingerprintOutput, VoiceData } from "../types/voice";
import type { InboxResponse } from "../types/inbox";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown) {
    super(`API ${status}: ${typeof payload === "string" ? payload : JSON.stringify(payload)}`);
    this.name = "ApiError";
  }
}

const safeJson = (s: string): unknown => {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
};

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(opts?.headers ?? {}),
    },
  });

  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    window.location.href = "/login";
    throw new ApiError(401, "");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeJson(text) : null;

  if (!res.ok) throw new ApiError(res.status, payload ?? text);
  return payload as T;
}

// ─────────── Domain types (espelho do backend) ───────────

export interface CookieHealth {
  capturedAt: string;
  ageDays: number;
  status: "fresh" | "warn" | "expired";
}

export interface SystemConfig {
  forceDraftMode: boolean;
  linkedinConnected: boolean;
  bannerMessage: string | null;
  scraperEnabled: boolean;
  operationalAlertsEnabled?: boolean;
  cookieHealth?: CookieHealth | null;
}

export type ScrapeTarget = "feed" | "messages";

// Mirrors backend/src/types.ts ScrapeRun (audit T3 tracks this duplication).
export interface ScrapeRun {
  runId: string;
  target: ScrapeTarget;
  status: "running" | "done" | "failed";
  collected: number;
  error?: string | null;
  bypassed: string[];
  startedAt: string;
  finishedAt?: string;
}

export interface DraftAlternative {
  label: "A" | "B" | "C";
  content: string;
  format: string;
  theme: string;
  hook: string;
  body: string;
  cta: string;
  hashtags: string[];
  rankerScore: number;
}

export interface ImageCandidate {
  id: string;
  s3Key: string;
  prompt: string;
  generatedAt: string;
  model: string;
  idempotencyKey: string;
  linkedinAssetUrn?: string;
}

export interface Draft {
  PK: string;
  SK: string;
  id: string;
  status: "draft" | "approved" | "scheduled" | "paused" | "publishing" | "published" | "failed" | "rejected" | "deleted";
  content: string;
  format: string;
  theme: string;
  hook: string;
  body: string;
  cta: string;
  hashtags: string[];
  createdAt: string;
  approvedAt?: string;
  scheduledAt?: string;
  publishedAt?: string;
  linkedinPostId?: string;
  linkedinPostUrn?: string;
  idempotencyKey: string;
  editDiff?: { original: string; final: string };
  researchCitations?: Array<{ url: string; title: string; accessedAt: string }>;
  sourceTopicPrompt?: string;
  imageCandidates?: ImageCandidate[];
  selectedImageId?: string;
  alternatives?: DraftAlternative[];
  rankerScore?: number;
  rankerReason?: string;
}

export interface SessionInfo {
  email: string;
}

export interface PasskeyInfo {
  credentialId: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

// ─────────── API surface ───────────

export const api = {
  auth: {
    requestLink: (email: string) =>
      request<{ sent: true }>("/api/auth/request", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    verify: (token: string) =>
      request<SessionInfo>("/api/auth/verify", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
    me: () => request<SessionInfo>("/api/auth/me"),
    logout: () => request<{ loggedOut: true }>("/api/auth/logout", { method: "POST" }),
    passkey: {
      registerOptions: () =>
        request<PublicKeyCredentialCreationOptionsJSON>("/api/auth/passkey/register/options", {
          method: "POST",
        }),
      registerVerify: (response: RegistrationResponseJSON, deviceName?: string) =>
        request<{ registered: true }>("/api/auth/passkey/register/verify", {
          method: "POST",
          body: JSON.stringify({ response, deviceName }),
        }),
      loginOptions: () =>
        request<PublicKeyCredentialRequestOptionsJSON>("/api/auth/passkey/login/options", {
          method: "POST",
        }),
      loginVerify: (response: AuthenticationResponseJSON) =>
        request<SessionInfo>("/api/auth/passkey/login/verify", {
          method: "POST",
          body: JSON.stringify({ response }),
        }),
      list: () => request<PasskeyInfo[]>("/api/auth/passkey/list"),
      remove: (credentialId: string) =>
        request<{ removed: true }>("/api/auth/passkey/remove", {
          method: "POST",
          body: JSON.stringify({ credentialId }),
        }),
    },
  },
  drafts: {
    list: () => request<Draft[]>("/api/drafts"),
    get: (id: string) => request<Draft>(`/api/drafts/${id}`),
    update: (id: string, content: string) =>
      request<Draft>(`/api/drafts/${id}`, {
        method: "PUT",
        body: JSON.stringify({ content }),
      }),
    approve: (id: string, scheduledAt?: string) =>
      request<Draft>(`/api/drafts/${id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scheduledAt }),
      }),
    reject: (id: string) =>
      request<{ rejected: true }>(`/api/drafts/${id}/reject`, { method: "POST" }),
    promoteAlternative: (id: string, label: "A" | "B" | "C") =>
      request<{ ok: true; promoted: string }>(
        `/api/drafts/${id}/promote-alternative`,
        { method: "PUT", body: JSON.stringify({ label }) }
      ),
    publishNow: (id: string) =>
      request<{ ok: true }>(`/api/drafts/${id}/publish-now`, { method: "POST" }),
    reschedule: (id: string, scheduledAt: string) =>
      request<{ ok: true }>(`/api/drafts/${id}/reschedule`, { method: "POST", body: JSON.stringify({ scheduledAt }) }),
    unschedule: (id: string) =>
      request<{ ok: true }>(`/api/drafts/${id}/unschedule`, { method: "POST" }),
    retry: (id: string) =>
      request<{ ok: true }>(`/api/drafts/${id}/retry`, { method: "POST" }),
    del: (id: string) =>
      request<{ ok: true }>(`/api/drafts/${id}`, { method: "DELETE" }),
    delPublished: (id: string) =>
      request<{ ok: true }>(`/api/published/${id}`, { method: "DELETE" }),
  },
  posts: {
    scheduled: () => request<Draft[]>("/api/scheduled"),
    published: () => request<Draft[]>("/api/published"),
  },
  generation: {
    now: (directive?: string) =>
      request<{ batchId: string; executionArn: string }>("/api/generate-now", {
        method: "POST",
        body: JSON.stringify({ directive }),
      }),
  },
  voice: {
    get: () => request<VoiceData>("/api/voice"),
    apply: (profile: FingerprintOutput, systemPrompt: string) =>
      request<{ version: number }>("/api/voice/apply", {
        method: "POST",
        body: JSON.stringify({ profile, systemPrompt }),
      }),
  },
  system: {
    get: () => request<SystemConfig>("/api/system-config"),
    patch: (patch: Partial<SystemConfig>) =>
      request<SystemConfig>("/api/system-config", { method: "PATCH", body: JSON.stringify(patch) }),
  },
  linkedin: {
    status: () => request<{ connected: boolean; expiresAt?: string; name?: string; avatar?: string | null; expiresInDays?: number; expired?: boolean }>("/api/auth/linkedin/status"),
  },
  inbox: {
    list: () => request<InboxResponse>("/api/inbox"),
    markReplied: (engId: string) =>
      request<{ ok: true }>(`/api/inbox/${engId}/mark-replied`, { method: "POST" }),
    discard: (engId: string) =>
      request<{ ok: true }>(`/api/inbox/${engId}/discard`, { method: "POST" }),
    // Operator-triggered run. Returns 202 immediately with the run row; the
    // scraper outlives the API's 60s ceiling, so poll scrapeRun for the outcome.
    scrapeNow: (target: ScrapeTarget) =>
      request<ScrapeRun>("/api/inbox/scrape-now", {
        method: "POST",
        body: JSON.stringify({ target }),
      }),
    scrapeRun: (runId: string) => request<ScrapeRun>(`/api/inbox/scrape-run/${runId}`),
  },
  images: {
    suggestPrompt: (draftId: string) =>
      request<{ prompt: string }>(`/api/drafts/${draftId}/image-prompt`, { method: "POST" }),

    select: (draftId: string, candidateId: string | null) =>
      request<{ ok: true; selectedImageId: string | null }>(
        `/api/drafts/${draftId}/selected-image`,
        { method: "PUT", body: JSON.stringify({ candidateId }) }
      ),

    getPreviewUrl: (draftId: string, candidateId: string) =>
      request<{ url: string; expiresIn: number }>(
        `/api/drafts/${draftId}/images/${candidateId}/preview-url`,
        { method: "GET" }
      ),
  },
};

// ─────────── Top-level voice helpers (para hooks) ───────────

export const getVoice = (): Promise<VoiceData> => api.voice.get();

export const applyVoice = (
  profile: FingerprintOutput,
  systemPrompt: string
): Promise<{ version: number }> => api.voice.apply(profile, systemPrompt);
